import { useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { buildProductHandlesQuery } from '../components/product/data/productQuery';
import { getCornerImage } from '@/utils/utils';
import { ImageEdge } from 'shopify-storefront-api-typings';

type Variant = {
  tile?: {
    value: string;
  };
  [key: string]: any;
};

type TileProduct = {
  handle: string;
  title: string;
  images: {
    edges: ImageEdge[];
  };
  metafields?: Array<{
    key: string;
    value: string;
    namespace: string;
    id: string;
  }>;
};

export const useVariantTiles = (variants: Variant[]) => {
  const tileHandles = useMemo(() => {
    if (!variants?.length) return [];

    return variants
      .filter((variant) => variant?.tile?.value)
      .map((variant) => {
        try {
          const parsedTiles = JSON.parse(variant.tile!.value);
          if (Array.isArray(parsedTiles) && parsedTiles.length > 0) {
            return parsedTiles[0].split(':')[0];
          }
          return null;
        } catch (e) {
          console.error('Error parsing tile value:', e);
          return null;
        }
      })
      .filter(Boolean) as string[];
  }, [variants]);

  const dynamicQuery = useMemo(() => {
    return buildProductHandlesQuery(tileHandles);
  }, [tileHandles]);

  const { data, loading, error } = useQuery(dynamicQuery, {
    skip: !tileHandles.length,
  });

  const tileProducts = useMemo(() => {
    if (!data) return [];

    return Object.entries(data)
      .filter(([key]) => key.startsWith('p'))
      .map(([_, product]) => product as TileProduct)
      .filter(Boolean);
  }, [data]);

  const tileCornerImages = useMemo(() => {
    if (!tileProducts.length) return [];

    return tileProducts.map((product) => ({
      handle: product.handle,
      image: getCornerImage(product?.images?.edges),
      title: product?.title || '',
      matType: product?.metafields?.find((m) => m?.key === 'mat_type')?.value,
    }));
  }, [tileProducts]);

  return {
    tileHandles,
    tileProducts,
    tileCornerImages,
    loading,
    error,
    originalVariants: variants,
  };
};
